import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, PhoneIncoming, PhoneOff } from "lucide-react";
import type { CallState, IncomingCallInfo } from "../../hooks/useTwilioDevice";

interface IncomingCallOverlayProps {
  incomingCall: IncomingCallInfo | null;
  callState: CallState;
  onAccept: () => void;
  onReject: () => void;
}

const IncomingCallOverlay: React.FC<IncomingCallOverlayProps> = ({
  incomingCall,
  callState,
  onAccept,
  onReject,
}) => {
  const isVisible =
    incomingCall !== null &&
    callState.status === "ringing" &&
    callState.direction === "inbound";

  const displayNumber =
    callState.remoteName || callState.remoteNumber || incomingCall?.from || "Unknown";

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            className="bg-white rounded-xl shadow-2xl max-w-sm w-full overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-50 to-white px-6 py-6 text-center">
              <div className="mx-auto h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4 animate-pulse">
                <Phone className="h-8 w-8 text-emerald-700" />
              </div>
              <h2 className="text-lg font-bold text-slate-800">
                Incoming Call
              </h2>
              <p className="text-2xl font-bold text-slate-900 mt-2">
                {displayNumber}
              </p>
              {callState.remoteName && callState.remoteNumber && (
                <p className="text-sm text-slate-500 mt-1">
                  {callState.remoteNumber}
                </p>
              )}
            </div>

            {/* Buttons */}
            <div className="px-6 py-5 flex items-center gap-4">
              <button
                onClick={onAccept}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold transition-colors"
              >
                <PhoneIncoming className="h-5 w-5" />
                Accept
              </button>
              <button
                onClick={onReject}
                className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-bold transition-colors"
              >
                <PhoneOff className="h-5 w-5" />
                Decline
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default IncomingCallOverlay;
